/**
 * @windrop/ui design tokens — Bible §13.3 "Design system foundations" and §13.2 motion budgets.
 *
 * Colour roles are semantic. Organizer, prize and sponsor brand colours are mapped onto roles ONLY after an
 * automated WCAG 2.2 contrast check; a brand colour that fails falls back to an approved neutral (§13.3 theme
 * rule, Appendix A "Sponsor asset fails contrast"). `applyTheme()` is the single entry point apps use.
 */

export type ThemeMode = 'dark' | 'light';

export type ColorRole =
  | 'canvas'
  | 'surface'
  | 'surface-elevated'
  | 'text-primary'
  | 'text-secondary'
  | 'border'
  | 'action'
  | 'action-contrast'
  | 'focus'
  | 'success'
  | 'warning'
  | 'error'
  | 'sponsor-accent'
  | 'sponsor-accent-contrast'
  | 'prize-accent'
  | 'prize-accent-contrast';

export const COLOR_ROLES: readonly ColorRole[] = [
  'canvas',
  'surface',
  'surface-elevated',
  'text-primary',
  'text-secondary',
  'border',
  'action',
  'action-contrast',
  'focus',
  'success',
  'warning',
  'error',
  'sponsor-accent',
  'sponsor-accent-contrast',
  'prize-accent',
  'prize-accent-contrast',
];

/** Approved neutral palettes (§13.3). Every pair below is verified ≥4.5:1 in tokens.test.ts. */
export const NEUTRALS: Record<ThemeMode, Record<ColorRole, string>> = {
  dark: {
    canvas: '#0B0D12',
    surface: '#151926',
    'surface-elevated': '#1C2133',
    'text-primary': '#F4F6FB',
    'text-secondary': '#A9B1C6',
    border: '#2A3147',
    action: '#E8ECF5',
    'action-contrast': '#0B0D12',
    focus: '#FFFFFF',
    success: '#4ADE80',
    warning: '#FBBF24',
    error: '#F87171',
    'sponsor-accent': '#C9D1E3',
    'sponsor-accent-contrast': '#0B0D12',
    'prize-accent': '#E8ECF5',
    'prize-accent-contrast': '#0B0D12',
  },
  light: {
    canvas: '#FFFFFF',
    surface: '#F4F6FA',
    'surface-elevated': '#FFFFFF',
    'text-primary': '#111318',
    'text-secondary': '#4B5267',
    border: '#D4D9E4',
    action: '#111318',
    'action-contrast': '#FFFFFF',
    focus: '#111318',
    success: '#15803D',
    warning: '#B45309',
    error: '#B91C1C',
    'sponsor-accent': '#3B4256',
    'sponsor-accent-contrast': '#FFFFFF',
    'prize-accent': '#111318',
    'prize-accent-contrast': '#FFFFFF',
  },
};

/** WCAG 2.2 AA thresholds (§13.3 Contrast). */
export const CONTRAST = { text: 4.5, largeOrUi: 3 } as const;

/** Typography (§13.3): Inter body 16px min, Bricolage Grotesque display 32–56px responsive. */
export const TYPOGRAPHY = {
  bodyFamily: "'Inter Variable', Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  displayFamily: "'Bricolage Grotesque Variable', 'Inter Variable', system-ui, sans-serif",
  bodyMinPx: 16,
  bodyLineHeight: 1.5,
  displayMinPx: 32,
  displayMaxPx: 56,
  /** CSS clamp used for display sizes between 32 and 56px. */
  displayClamp: 'clamp(2rem, 1.25rem + 3.2vw, 3.5rem)',
  measureCh: 68,
} as const;

/** Spacing: 4px base; gaps 8/12/16/24/32 (§13.3). */
export const SPACING = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40, 12: 48 } as const;

/** Radii 8–12px, one elevation level (§13.3). */
export const RADII = { xs: 6, sm: 8, md: 12, lg: 16, xl: 24, pill: 999 } as const;
export const ELEVATION = {
  one: '0 8px 24px -12px rgba(0, 0, 0, 0.55), 0 1px 0 0 rgba(255, 255, 255, 0.04) inset',
  oneLight: '0 8px 24px -14px rgba(16, 24, 40, 0.28)',
} as const;

/** Touch targets 44×44 CSS px with ≥8px separation (§13.3). */
export const TOUCH = { minSize: 44, minGap: 8 } as const;

/**
 * Motion duration budgets (§13.2 table). Values in ms. Calm mode uses 0 for every decorative duration and an
 * instant state change with identical text.
 */
export const MOTION = {
  controlMin: 80,
  control: 100,
  controlMax: 120,
  transitionMin: 180,
  transition: 200,
  transitionMax: 240,
  wheelMin: 2500,
  wheel: 3200,
  wheelMax: 4000,
  confettiMax: 3000,
  /** WCAG 2.3.1: never more than three flashes in any one-second period. */
  maxFlashesPerSecond: 3,
} as const;

// ---------------------------------------------------------------------------------------------------------------
// Contrast maths (WCAG 2.x relative luminance)
// ---------------------------------------------------------------------------------------------------------------

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parses `#RGB`, `#RRGGBB` or `#RRGGBBAA` (alpha ignored). Returns null for anything else. */
export function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1] ?? '';
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return { r, g, b };
}

export function toHex({ r, g, b }: Rgb): string {
  const c = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) throw new Error(`Not a hex colour: ${hex}`);
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** WCAG contrast ratio between two hex colours, 1..21. Order does not matter. */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  const [hi, lo] = a >= b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/** Picks the approved neutral text colour (dark or light) with the best contrast on `bg`. */
export function bestForeground(bg: string, mode: ThemeMode): string {
  const darkText = NEUTRALS.light['text-primary'];
  const lightText = NEUTRALS.dark['text-primary'];
  const preferred = mode === 'dark' ? [lightText, darkText] : [darkText, lightText];
  const [a, b] = preferred as [string, string];
  return contrastRatio(bg, a) >= contrastRatio(bg, b) ? a : b;
}

// ---------------------------------------------------------------------------------------------------------------
// Theme application
// ---------------------------------------------------------------------------------------------------------------

export interface ThemeInput {
  /** Organizer brand colour → `action` + `focus` roles (when it passes). */
  organizerAccent?: string | null;
  /** Hero prize colour → `prize-accent` role. */
  prizeAccent?: string | null;
  /** Presenting sponsor colour → `sponsor-accent` role. */
  sponsorAccent?: string | null;
  mode?: ThemeMode;
  /** Element that receives the CSS custom properties. Defaults to `document.documentElement` when present. */
  root?: HTMLElement | null;
}

export type AccentRole = 'action' | 'prize-accent' | 'sponsor-accent' | 'focus';

export interface ResolvedAccent {
  role: AccentRole;
  /** The colour actually applied. */
  value: string;
  /** Foreground that passes ≥4.5:1 on `value` (not used for `focus`). */
  contrast: string;
  source: 'brand' | 'neutral';
  /** Lowest ratio among the checks performed for this role. */
  ratio: number;
  /** Why the brand colour was rejected, when `source === 'neutral'` and a brand colour was requested. */
  reason?: string;
}

export interface ResolvedTheme {
  mode: ThemeMode;
  /** CSS custom properties, `--wd-<role>` → value. */
  vars: Record<string, string>;
  accents: Record<AccentRole, ResolvedAccent>;
  /** Roles whose brand colour failed and fell back to a neutral. Empty when everything passed. */
  fallbacks: ResolvedAccent[];
}

export const CSS_VAR_PREFIX = '--wd-';

export function cssVar(role: ColorRole): string {
  return `${CSS_VAR_PREFIX}${role}`;
}

/**
 * Checks a brand colour for an accent role. An accent is used two ways: as a filled background (needs a text
 * foreground ≥4.5:1) and as a large-text / UI colour on the canvas and surfaces (≥3:1). Both must hold.
 */
export function resolveAccent(
  role: AccentRole,
  requested: string | null | undefined,
  mode: ThemeMode,
): ResolvedAccent {
  const base = NEUTRALS[mode];
  const neutral: ResolvedAccent = {
    role,
    value: base[role],
    contrast: role === 'focus' ? base['text-primary'] : bestForeground(base[role], mode),
    source: 'neutral',
    ratio: Math.min(
      role === 'focus' ? Infinity : contrastRatio(base[role], bestForeground(base[role], mode)),
      contrastRatio(base[role], base.canvas),
      contrastRatio(base[role], base.surface),
    ),
  };
  if (!requested) return neutral;
  const rgb = parseHex(requested);
  if (!rgb) return { ...neutral, reason: `"${requested}" is not a hex colour` };
  const value = toHex(rgb);
  const onCanvas = contrastRatio(value, base.canvas);
  const onSurface = contrastRatio(value, base.surface);
  const fg = bestForeground(value, mode);
  const fgRatio = contrastRatio(value, fg);
  const uiRatio = Math.min(onCanvas, onSurface);
  if (uiRatio < CONTRAST.largeOrUi) {
    return {
      ...neutral,
      reason: `${value} is ${uiRatio.toFixed(2)}:1 against the ${mode} canvas/surface (needs ≥${CONTRAST.largeOrUi}:1)`,
    };
  }
  if (role !== 'focus' && fgRatio < CONTRAST.text) {
    return {
      ...neutral,
      reason: `no approved text colour reaches ${CONTRAST.text}:1 on ${value} (best ${fgRatio.toFixed(2)}:1)`,
    };
  }
  return {
    role,
    value,
    contrast: fg,
    source: 'brand',
    ratio: Math.min(uiRatio, role === 'focus' ? Infinity : fgRatio),
  };
}

/** Builds the full variable map for a theme without touching the DOM. */
export function buildTheme(input: ThemeInput = {}): ResolvedTheme {
  const mode: ThemeMode = input.mode ?? 'dark';
  const base = NEUTRALS[mode];
  const action = resolveAccent('action', input.organizerAccent, mode);
  const focus = resolveAccent('focus', input.organizerAccent, mode);
  const prize = resolveAccent('prize-accent', input.prizeAccent, mode);
  const sponsor = resolveAccent('sponsor-accent', input.sponsorAccent, mode);
  const roles: Record<ColorRole, string> = {
    ...base,
    action: action.value,
    'action-contrast': action.contrast,
    focus: focus.value,
    'prize-accent': prize.value,
    'prize-accent-contrast': prize.contrast,
    'sponsor-accent': sponsor.value,
    'sponsor-accent-contrast': sponsor.contrast,
  };
  const vars: Record<string, string> = {};
  for (const role of COLOR_ROLES) vars[cssVar(role)] = roles[role];
  const accents = { action, focus, 'prize-accent': prize, 'sponsor-accent': sponsor };
  const fallbacks = Object.values(accents).filter((a) => a.source === 'neutral' && a.reason);
  return { mode, vars, accents, fallbacks };
}

/**
 * Applies a lottery theme (§13.3). Brand colours that fail contrast are replaced by approved neutrals and
 * reported in `fallbacks` so the console can show "sponsor asset fails contrast" (Appendix A) before publish.
 */
export function applyTheme(input: ThemeInput = {}): ResolvedTheme {
  const theme = buildTheme(input);
  const root = input.root ?? (typeof document !== 'undefined' ? document.documentElement : null);
  if (root) {
    for (const [k, v] of Object.entries(theme.vars)) root.style.setProperty(k, v);
    root.setAttribute('data-theme', theme.mode);
    root.style.colorScheme = theme.mode;
  }
  return theme;
}

/** Removes an applied theme (used when leaving a lottery page). */
export function clearTheme(root?: HTMLElement | null): void {
  const el = root ?? (typeof document !== 'undefined' ? document.documentElement : null);
  if (!el) return;
  for (const role of COLOR_ROLES) el.style.removeProperty(cssVar(role));
  el.removeAttribute('data-theme');
  el.style.removeProperty('color-scheme');
}

export const tokens = {
  colors: NEUTRALS,
  contrast: CONTRAST,
  typography: TYPOGRAPHY,
  spacing: SPACING,
  radii: RADII,
  elevation: ELEVATION,
  touch: TOUCH,
  motion: MOTION,
} as const;
